import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { compose } from "recompact"
import { Field, getFormValues, reduxForm, initialize } from "redux-form"

import FieldSet from "@infotech/uikit-fieldset"
import { CloseIcon, LoaderIcon } from "@infotech/uikit-icons"
import { Box } from "@infotech/uikit-grid"
import { H3, P } from "@infotech/uikit-text"
import { PrimaryButton, SecondaryButton } from "@infotech/uikit-button"
import Spin from "@infotech/uikit-spin"

import ModalConfirm from "src/components/modals/ModalConfirm"
import Input from "src/components/Input"
import InputNumberHOC from "src/components/InputNumberHOC"
import GlobalEscapeHandle from "src/components/GlobalEscapeHandle"
import Checkbox from "src/components/Checkbox"

import { read } from "src/engine/modules/resourceTypeFields/actions/creators/read"
import { update } from "src/engine/modules/resourceTypeFields/actions/creators/update"
import { create } from "src/engine/modules/resourceTypeFields/actions/creators/create"

import noop from "lodash/noop"
import get from "lodash/get"

import styles from "./styles.css"

const FORM_NAME = `fieldForm`
const selectValues = getFormValues(FORM_NAME)

const getTestAttribute = name => `planner-resource-type-custom-field-${name}`

const CustomField = ({
  data,
  close,
  dirty,
  refreshGrid,
  create,
  update,
  read,
  formValues,
  field,
  initialize,
  setModalData,
  status,
  errors,
}) => {
  const { id, resourceTypeId } = data
  const isNew = id === `new`
  const { completed, pending } = status
  const isDisabled = pending
  // запрашиваем данные поля при редактировании
  useEffect(() => {
    if (!isNew) {
      read(id)
    }
  }, [id])
  // валидация
  const [shouldValidate, toggleShouldValidate] = useState(true)
  useEffect(() => {
    if (shouldValidate) {
      toggleShouldValidate(false)
    }
  }, [
    get(formValues, `name`),
    get(formValues, `label`),
    get(formValues, `index`),
    get(formValues, `type`),
  ])

  const validate =
    shouldValidate && !isDisabled
      ? {
          name: get(errors, `name`) && get(errors, `name`).join(`. `),
          label: get(errors, `label`) && get(errors, `label`).join(`. `),
        }
      : null

  const [isConfirmationModalOpen, toggleConfirmationModal] = useState(false)
  const onSubmit = () => {
    toggleShouldValidate(true)
    if (isNew) {
      create({
        rtid: resourceTypeId,
        data: formValues,
        refreshGrid,
      })
    } else {
      update({
        id: get(field, `id`) || get(data, `id`),
        data: formValues,
        refreshGrid,
      })
    }
  }
  useEffect(() => {
    if (completed) {
      setModalData(get(field, `data`))
      initialize(get(field, `data`))
    }
  }, [status])
  return (
    <Box loose flex column className={styles.FieldContentWrapper}>
      <Box
        loose
        flex
        className={styles.FieldFormHeader}
        pt="2md"
        pl="2md"
        pr="2md"
      >
        <H3>{isNew ? `Создание` : `Редактирование`} поля</H3>
        <span onClick={dirty ? toggleConfirmationModal : close}>
          <CloseIcon className={styles.CloseCross} color="#7f7f7f" />
        </span>
      </Box>
      <Box loose p="2md" className={styles.FormFieldsWrapper}>
        <Box mb={get(validate, `name`) ? `-2md` : null} loose>
          <div data-testid={getTestAttribute(`name`)}>
            <FieldSet caption="Наименование">
              <Field
                component={Input}
                name="name"
                error={get(validate, `name`)}
                props={{
                  placeholder: `Введите наименование поля`,
                  disabled: isDisabled,
                }}
              />
            </FieldSet>
          </div>
        </Box>
        <Box mb={get(validate, `label`) ? `-2md` : null} loose>
          <div data-testid={getTestAttribute(`label`)}>
            <FieldSet caption="Описание">
              <Field
                component={Input}
                name="label"
                error={get(validate, `label`)}
                props={{
                  placeholder: `Введите описание`,
                  disabled: isDisabled,
                }}
              />
            </FieldSet>
          </div>
        </Box>
        <Box loose flex>
          <Box loose w="200px" pr="2md">
            <div data-testid={getTestAttribute(`type`)}>
              <FieldSet caption="Тип поля">
                <Field
                  component={Input}
                  name="type"
                  props={{
                    // TODO: hardcode
                    disabled: true,
                  }}
                />
              </FieldSet>
            </div>
          </Box>
          <Box loose w="100px">
            <div data-testid={getTestAttribute(`index`)}>
              <FieldSet caption="Позиция поля">
                <Field
                  component={InputNumberHOC}
                  name="index"
                  props={{
                    min: 1,
                    disabled: isDisabled,
                  }}
                />
              </FieldSet>
            </div>
          </Box>
        </Box>
        <Box loose w="150px">
          <div data-testid={getTestAttribute(`required`)}>
            <Field
              component={Checkbox}
              name="required"
              props={{
                disabled: isDisabled,
                label: `Обязательное поле`,
              }}
            />
          </div>
        </Box>
        {pending && (
          <Box flex align="center" justify="center">
            <Spin duration="fast">
              <LoaderIcon color="#2880ff" />
            </Spin>
          </Box>
        )}
      </Box>
      <Box loose flex className={styles.FieldFormBottom} p="2md">
        <PrimaryButton
          className={styles.Buttons}
          onClick={onSubmit}
          disabled={!dirty}
        >
          {isNew ? `Создать` : `Сохранить`}
        </PrimaryButton>
        <Box loose pl="2md">
          <SecondaryButton
            className={styles.Buttons}
            onClick={dirty ? toggleConfirmationModal : close}
          >
            Отмена
          </SecondaryButton>
        </Box>
      </Box>
      <GlobalEscapeHandle
        onEscapePress={dirty ? toggleConfirmationModal : close}
      />
      <ModalConfirm
        isOpen={isConfirmationModalOpen}
        title="Закрыть без сохранения?"
        buttonConf={[
          {
            text: `Закрыть`,
            onClick: dirty ? close : noop,
            isOk: true,
            done: true,
            disabled: false,
          },
          {
            text: `Отмена`,
            onClick: () => toggleConfirmationModal(false),
            disabled: false,
          },
        ]}
        isClose
      >
        <div className={styles[`modal-message-wrapper`]}>
          <P className={styles[`modal-message-text`]}>
            Все сделанные вами изменения будут утеряны
          </P>
        </div>
      </ModalConfirm>
    </Box>
  )
}

CustomField.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    resourceTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    required: PropTypes.bool.isRequired,
    index: PropTypes.number.isRequired,
  }).isRequired,
  create: PropTypes.func.isRequired,
  update: PropTypes.func.isRequired,
  read: PropTypes.func.isRequired,
  close: PropTypes.func.isRequired,
  refreshGrid: PropTypes.func.isRequired,

  dirty: PropTypes.bool.isRequired,
  formValues: PropTypes.shape({
    name: PropTypes.string,
    label: PropTypes.string,
    type: PropTypes.string,
    required: PropTypes.bool,
    index: PropTypes.number,
  }),
  field: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    resourceTypeId: PropTypes.number,
    name: PropTypes.string,
    label: PropTypes.string,
    type: PropTypes.string,
    required: PropTypes.bool,
    index: PropTypes.number,
  }),
  initialize: PropTypes.func.isRequired,
  setModalData: PropTypes.func.isRequired,
  status: PropTypes.shape({
    completed: PropTypes.bool.isRequired,
    pending: PropTypes.bool.isRequired,
  }),
  errors: PropTypes.shape({
    name: PropTypes.arrayOf(PropTypes.string),
    label: PropTypes.arrayOf(PropTypes.string),
  }),
}

CustomField.defaultProps = {
  field: {},
  formValues: {},
  status: {},
  errors: [],
}

const enhancer = compose(
  connect(
    (state, props) => ({
      form: FORM_NAME,
      initialValues: get(props, `data`),
      formValues: selectValues(state),
      field: get(state, `resourceTypeFields`),
      status: get(state, `resourceTypeFields.status`),
      errors: get(state, `resourceTypeFields.error`),
    }),
    {
      read,
      create,
      update,
      initialize,
    },
  ),
  reduxForm(),
)

export default enhancer(CustomField)
